import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { AuthLayout } from '../components/AuthLayout'
import { Button } from '../components/Button'
import { Field } from '../components/Field'
import { getSupabase } from '../lib/supabase'

const googleEnabled = import.meta.env.VITE_SUPABASE_GOOGLE_ENABLED === 'true'

export function LoginPage() {
  const { session, loading } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!loading && session) return <Navigate to="/" replace />

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    const { error: signInError } = await getSupabase().auth.signInWithPassword({
      email: email.trim(),
      password,
    })

    if (signInError) {
      setError(
        signInError.message === 'Invalid login credentials'
          ? 'That email or password combination didn’t work.'
          : signInError.message,
      )
      setSubmitting(false)
      return
    }

    navigate('/', { replace: true })
  }

  async function handleGoogle() {
    setError(null)
    const { error: oauthError } = await getSupabase().auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/` },
    })
    if (oauthError) setError(oauthError.message)
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your nMach workspace."
      footer={
        <>
          <p>
            New here?{' '}
            <Link to="/signup" className="font-medium text-cobalt underline-offset-4 hover:underline">
              Create an account
            </Link>
          </p>
          <p>
            <Link to="/forgot-password" className="text-ink-muted underline-offset-4 hover:text-ink hover:underline">
              Forgot your password?
            </Link>
          </p>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Field
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Field
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={error}
        />
        <Button type="submit" size="lg" fullWidth loading={submitting}>
          Sign in
        </Button>

        {googleEnabled && (
          <>
            <div className="flex items-center gap-3 text-small text-ink-faint" aria-hidden="true">
              <span className="h-px flex-1 bg-line" />
              or
              <span className="h-px flex-1 bg-line" />
            </div>
            <Button type="button" variant="secondary" size="lg" fullWidth onClick={() => void handleGoogle()}>
              Continue with Google
            </Button>
          </>
        )}
      </form>
    </AuthLayout>
  )
}
